// HomeJanetDock — issue #176 requirement #1: Janet visibly present on the
// default dashboard with an accessible text composer and an explicit voice
// doorway — never hidden behind FAB discovery or a separate chat page.
// Wraps the SAME JanetChatPanel every other host uses (DealInspector's own
// layout="docked" pattern) — no second chat engine, no second truth store,
// no auto-start of voice or messages: JanetChatPanel never sends or listens
// until a person taps something inside it.
//
// Desktop / ordinary width: an always-visible persistent dock (HomeJanetDockDesktop).
// Mobile — "intentionally condensed" (Aaron's bridge/adaptive-density
// direction, 2026-09-30): a compact bar by default, expandable in place to
// the full conversation surface (HomeJanetDockMobile). Collapsing does not
// lose the conversation — JanetChatPanel/useJanetMemory persist per topic
// independent of this component's own expand/collapse UI state.
//
// Compact bar = two SIBLING doorways, not one button with a decorative mic
// glyph (2026-09-30 repair, Product Review finding on issue #178): "Ask
// Janet" opens straight to the text composer, and a separate, accurately
// labeled "Talk to Janet" button opens the panel AND starts the SAME live
// voice session the panel's own header "Live voice" control already drives
// (JanetChatPanel's `startLiveVoiceOnMount` prop → handleStartLiveVoice —
// no second voice implementation). The tap on "Talk to Janet" IS the user
// gesture that authorizes starting it; nothing here starts voice on mount
// or on collapse/expand via the text doorway. Neither button claims a hold
// gesture — there isn't one at this altitude, so none is advertised.
//
// "Talk to Janet" also acquires the mic + unlocks audio SYNCHRONOUSLY,
// right here in its own click handler (2026-09-30 repair, Product Review
// finding: by the time JanetChatPanel's startLiveVoiceOnMount effect runs —
// after its own timer, handleStartLiveVoice's voice-prompt fetch, and
// useJanetRealtime.start's own session-token fetch — this tap's transient
// user activation has expired, and browsers like iOS Safari can reject a
// getUserMedia/AudioContext call made that late). createVoicePreflight()
// does the acquiring; the result is handed into JanetChatPanel as
// `voicePreflight` and from there straight into the EXISTING rt.start path
// (useJanetRealtime.js) as an optional pre-acquired input — still one voice
// implementation, only WHEN mic/audio are acquired changes. If the dock is
// collapsed (or unmounts) before that hand-off is ever claimed,
// releaseVoicePreflight stops the track and closes the context so nothing
// leaks; it's a safe no-op once start() has taken ownership.
//
// HomeJanetDockMobile also reports its own expand/collapse state up through
// JanetPageContext's `compactDockExpanded` (2026-09-30 repair, third pass,
// issue #178): Layout.jsx's fixed BugReportFAB shares this bottom corner
// below `lg`, and since this dock is a normal-flow element (not fixed) its
// EXPANDED panel's Send button can land under that fixed button at some
// viewport heights but not others. `compactDockExpanded` is the one signal
// that sibling has no other way to observe, so Layout.jsx can hide it for
// exactly as long as this dock is expanded.
import { useState, useRef, useEffect } from 'react'
import { ChevronDown, Mic } from 'lucide-react'
import JanetChatPanel from '../janet/JanetChatPanel'
import { useJanetPage } from '../../contexts/JanetPageContext'
import { createVoicePreflight, releaseVoicePreflight } from '../../hooks/useJanetRealtime'

export function HomeJanetDockDesktop({ onClose } = {}) {
  const { page, activeSessionId, pickTopic, newTopic } = useJanetPage()
  return (
    <div className="h-full min-h-0 flex flex-col" style={{ background: 'white' }}>
      <JanetChatPanel
        layout="docked"
        sessionId={activeSessionId}
        page={page}
        onNewTopic={() => newTopic()}
        onPickTopic={pickTopic}
        onClose={onClose}
        showTopics
      />
    </div>
  )
}

export function HomeJanetDockMobile() {
  const { page, activeSessionId, pickTopic, newTopic, setCompactDockExpanded } = useJanetPage()
  const [expanded, setExpanded] = useState(false)
  const [startVoice, setStartVoice] = useState(false)
  const preflightRef = useRef(null)

  const openText = () => { setStartVoice(false); setExpanded(true); setCompactDockExpanded(true) }

  const openVoice = () => {
    // The buttons below only render while collapsed, so a leftover
    // preflight here would mean a prior tap's hand-off was never claimed
    // and never collapsed either — release it defensively before starting
    // a fresh one.
    releaseVoicePreflight(preflightRef.current)
    preflightRef.current = createVoicePreflight()
    setStartVoice(true)
    setExpanded(true)
    setCompactDockExpanded(true)
  }

  const collapse = () => {
    // No-op once JanetChatPanel's own start() has claimed the preflight —
    // otherwise stops the pre-acquired track and closes the context so an
    // early collapse (tap Talk, immediately close) never leaks either.
    releaseVoicePreflight(preflightRef.current)
    preflightRef.current = null
    setExpanded(false)
    setStartVoice(false)
    setCompactDockExpanded(false)
  }

  // The dock can also unmount outright (e.g. navigating away from Home)
  // while a tap is still in flight — same leak risk as collapse(). Also
  // clears the shared expanded flag so a fixed sibling (BugReportFAB via
  // Layout.jsx) doesn't stay hidden after Home itself goes away.
  useEffect(() => () => {
    releaseVoicePreflight(preflightRef.current)
    setCompactDockExpanded(false)
  }, [setCompactDockExpanded])

  if (!expanded) {
    return (
      <div className="w-full flex items-stretch gap-2 mb-4">
        <button
          type="button"
          onClick={openText}
          className="flex-1 min-w-0 flex items-center gap-2.5 px-3 py-2.5 rounded-xl border text-left"
          style={{ borderColor: 'var(--ns-hair)', background: 'var(--ns-raise)' }}
          aria-label="Ask Janet — open text chat"
        >
          <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0" style={{ background: 'var(--ns-janet)' }}>
            <span className="text-xs font-bold text-white">✦</span>
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-[12.5px] font-semibold" style={{ color: 'var(--ns-ink)' }}>Ask Janet</span>
            <span className="block text-[10.5px] truncate" style={{ color: 'var(--ns-faint)' }}>
              Draft, brief, schedule — type or talk
            </span>
          </span>
          <ChevronDown size={15} style={{ color: 'var(--ns-faint)' }} />
        </button>
        <button
          type="button"
          onClick={openVoice}
          className="shrink-0 flex items-center gap-1.5 px-3 rounded-xl border"
          style={{ borderColor: 'var(--ns-hair)', background: 'var(--ns-raise)' }}
          aria-label="Talk to Janet — start a live voice conversation"
          title="Talk to Janet — start a live voice conversation"
        >
          <Mic size={15} style={{ color: 'var(--ns-janet)' }} />
          <span className="text-[11px] font-semibold" style={{ color: 'var(--ns-ink)' }}>Talk</span>
        </button>
      </div>
    )
  }

  return (
    <div className="mb-4 rounded-xl border overflow-hidden" style={{ borderColor: 'var(--ns-hair)', height: 460 }}>
      <JanetChatPanel
        layout="docked"
        sessionId={activeSessionId}
        page={page}
        onClose={collapse}
        onNewTopic={() => newTopic()}
        onPickTopic={pickTopic}
        showTopics
        startLiveVoiceOnMount={startVoice}
        voicePreflight={startVoice ? preflightRef.current : null}
      />
    </div>
  )
}
